import type { EmailChangeRequested, RequestEmailChangeInput } from "@closer/shared";
import { requestEmailChangeSchema } from "@closer/shared";
import { Button, Card, FormField, Input, PageHeader, useToast } from "@closer/ui";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { AccountClosureSection } from "./AccountClosureSection";
import { AiSecuritySignalsSection } from "./AiSecuritySignalsSection";
import { DevicesSection } from "./DevicesSection";
import { PasswordSection } from "./PasswordSection";
import { PrivacyRequestsSection } from "./PrivacyRequestsSection";
import { ProfileSection } from "./ProfileSection";
import { SecurityHistorySection } from "./SecurityHistorySection";
import { TwoFactorSection } from "./TwoFactorSection";

/**
 * Hesap ve Güvenlik (CLOS-225 `[048]`).
 *
 * ### Sayfa neden BURADA doğdu
 *
 * `[048]` kullanıcıya dönük bir yetenek: giriş adresini değiştirmek. Yalnız uç yazıp
 * ekranı atlasaydık, `[017]` ve `[020]`'de olan olurdu — API var, kullanıcı ulaşamıyor.
 * Sayfa bugün İKİ bölüm taşıyor: e-posta değişikliği ve güvenlik geçmişi (`[082]`).
 * 2FA (`[018]`), aktif oturumlar (`[020]`) ve şifre (`[021]`) kendi kartlarıyla burada.
 * Şifre kartı en son eklendi: uç aylardır hazırdı ama onu çağıran hiçbir yüzey yoktu ve
 * kullanıcı şifresini üründen değiştiremiyordu (kullanıcı bildirdi, 26 Ağustos).
 *
 * ### Adres HEMEN değişmiyor
 *
 * Sunucu 202 dönüyor: talep alındı, adres doğrulanana kadar eski. Ekran bunu açıkça
 * söylüyor — "değiştirildi" deseydik kullanıcı yeni adresiyle girmeyi dener ve
 * giremezdi.
 */
export function AccountSecurityPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [sonuc, setSonuc] = useState<EmailChangeRequested | null>(null);

  const form = useZodForm(requestEmailChangeSchema, {
    defaultValues: { newEmail: "", currentPassword: "", code: "" } as RequestEmailChangeInput,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      /** ⚠ Boş kod GÖNDERİLMİYOR: şema trim'li ve isteğe bağlı, boş metin isteği takardı. */
      const { code, ...gerisi } = values;
      const govde = code && code.trim().length > 0 ? { ...gerisi, code: code.trim() } : gerisi;
      const { data } = await api<EmailChangeRequested>("/api/profile/email-change", {
        method: "POST",
        body: JSON.stringify(govde),
      });
      setSonuc(data);
      form.reset({ newEmail: "", currentPassword: "", code: "" });
    } catch (error) {
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "İşlem tamamlanamadı. Lütfen tekrar deneyin.",
      );
    }
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title="Hesap ve Güvenlik"
        description="Giriş adresinizi, şifrenizi ve oturum güvenliğinizi buradan yönetin."
      />

      <Card title="E-posta Adresi">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-muted">
            Giriş adresiniz: <span className="font-medium text-fg">{user?.email}</span>
          </p>

          {sonuc ? (
            <div
              data-testid="talep-sonucu"
              role="status"
              className="rounded-md bg-success-soft p-3 text-sm text-fg-strong"
            >
              <p>
                {sonuc.newEmail} adresine bir doğrulama bağlantısı gönderildi. Bağlantıyı onaylayana
                kadar giriş adresiniz değişmez.
              </p>
              {/*
                ⚠ Davetler TAŞINMIYOR (`[036]`: "yaşam döngüsü sessizce değiştirilmez").
                Kullanıcı kaç davetin geride kalacağını ONAYLAMADAN ÖNCE öğreniyor —
                sessizce kaybetmek, davet edenin gönderdiği adresi arkasından değiştirmek
                olurdu.
              */}
              {sonuc.pendingInvitations > 0 ? (
                <p className="mt-2">
                  Eski adresinize gönderilmiş {sonuc.pendingInvitations} bekleyen davet var. Adres
                  değiştikten sonra bu davetler hesabınızda görünmeyecek.
                </p>
              ) : null}
            </div>
          ) : null}

          {/*
            ⚠ FORMUN ADI VAR (26 Ağustos): sayfada artık iki "Mevcut şifreniz" alanı duruyor
            — biri burada, biri şifre değiştirme bölümünde. Ekran okuyucu için de, test için
            de hangi formun içinde olunduğu ancak formun adıyla cevaplanabilir.
          */}
          <form
            aria-label="E-posta adresini değiştir"
            onSubmit={onSubmit}
            noValidate
            className="flex flex-col gap-4"
          >
            <FormField
              label="Yeni e-posta adresi"
              error={form.formState.errors.newEmail?.message}
              required
            >
              <Input type="email" autoComplete="email" {...form.register("newEmail")} />
            </FormField>

            {/*
              Parola HER ZAMAN isteniyor: oturumun varlığı yetmez. Oturum çalınmış olabilir
              ve giriş adresini değiştirmek, hesabı kalıcı olarak ele geçirmenin yolu.
            */}
            <FormField
              label="Mevcut şifreniz"
              error={form.formState.errors.currentPassword?.message}
              required
            >
              <Input
                type="password"
                autoComplete="current-password"
                {...form.register("currentPassword")}
              />
            </FormField>

            {/*
              ⚠ İKİNCİ FAKTÖR ALANI (BUG-022b · Mega E2E'de ölçüldü).

              Şifre kartıyla AYNI kusur: sunucu `second_factor_required` ile 403 veriyor ve
              şema `code` alanını taşıyor, ama formda alan yoktu — 2FA'sını açan kullanıcı
              giriş adresini üründen bir daha değiştiremiyordu. Güvenliğini artıran
              kullanıcının hesabını yönetme yeteneğini kaybetmesi, kartın tam tersi.

              ⚠ İsteğe bağlı ve boşken gövdeye GİRMİYOR (`AccountClosureSection` emsali).
            */}
            <FormField
              label="Doğrulama kodu (2FA açıksa)"
              error={form.formState.errors.code?.message}
            >
              <Input autoComplete="one-time-code" inputMode="numeric" {...form.register("code")} />
            </FormField>

            <Button type="submit" loading={form.formState.isSubmitting}>
              Doğrulama bağlantısı gönder
            </Button>
          </form>
        </div>
      </Card>

      {/*
        GÜVENLİK GEÇMİŞİ (CLOS-284 `[082]`). Aynı sayfada çünkü kullanıcının sorusu tek:
        "hesabım güvende mi?" Ayrı bir sayfaya koysaydık, e-posta değiştiren kullanıcı
        değişikliğin geçmişe düştüğünü görmek için başka bir yere gitmek zorunda kalırdı.
      */}
      {/*
        ŞİFRE (`[021]`) 2FA'dan ÖNCE: kullanıcı "hesabım güvende mi?" sorusuna önce en temel
        yerden bakıyor. Google ile açılmış hesapta bu kart, değiştirme yerine BELİRLEME
        yolunu gösteriyor.
      */}
      {/* ⚠ Profil bilgileri ŞİFREDEN ÖNCE: kullanıcı bu sayfaya çoğunlukla kendi
          bilgilerini düzeltmek için geliyor; güvenlik bölümleri altında kalıyor. */}
      <ProfileSection />

      <PasswordSection />

      {/* 2FA kurulumu (`[018]`/`[019]`) — güvenlik geçmişinden ÖNCE: önce yapılabilecek şey. */}
      <TwoFactorSection />

      {/* Aktif cihazlar geçmişten ÖNCE: geçmiş okunur, cihaz üzerinde eylem yapılır. */}
      <DevicesSection />

      {/*
        AI GÜVENLİK SİNYALLERİ (CLOS-287 `[191]`) — geçmişin HEMEN ÜSTÜNDE: sinyaller o
        geçmişin bir okuması ve kullanıcı sinyalden kaynağa bakabilmeli.
      */}
      <AiSecuritySignalsSection />

      <SecurityHistorySection />

      {/*
        Kişisel veri talepleri (CLOS-222 `[201]`) — kapatmadan ÖNCE: silme talebinin
        gerçekten tamamlanması genelde hesabın önce kapatılmasını gerektiriyor, kullanıcı
        bu bağı kapatma adımına gelmeden görmeli.
      */}
      <PrivacyRequestsSection />

      {/* Kapatma en SONDA: geri alınamaz ve gündelik bir işlem değil. */}
      <AccountClosureSection />
    </div>
  );
}
